import {Component} from 'preact';
import Provider from 'preact-context-provider';
import {client} from '@core/GraphQLProvider';
import Cookies from 'js-cookie';
import {Route, Switch, withRouter} from 'react-router-dom';
import dataLayer from '@upi/data-layer';
import appLogger from '@core/LoggingProvider';
import ProtectedRoute from '@components/common/ProtectedRoute';
import {$CONTENT_WINDOW} from '@config/selectors';
import {COOKIES} from '@util/StorageUtils';
import AccountSetUp from '@components/registration/AccountSetUp';
import OfficeDetails from '@components/registration/OfficeDetails';
import VerifyAccount from '@components/registration/VerifyAccount';
import NotValidated from '@components/registration/NotValidated';
import MultipleAddresses from '@components/registration/MultipleAddresses';
import AccountVerifiedNPI from '@components/registration/AccountVerifiedNPI';
import AccountRequiresVerification from '@components/registration/AccountRequiresVerification';
import RegistrationLocked from '@components/registration/RegistrationLocked';
import NewExistingDecision from '@components/registration/NewExistingDecision';
import NewsletterService from '@services/NewsletterService';

const initialState = {
    dentalLicense: '',
    emailAddress: '',
    password: '',
    dentistFullName: '',
    specialty: 'General Practice',
    practiceName: '',
    officePhone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    offers: false,
    accountNumber: '',
    invoiceId: '',
    addresses: [],

    dentalLicenseExists: false,
    step: 1,
    viewCount: 1,
    county: false,

    signUpLoading: false,
    signUpSubmitting: false,
    signUpSuccess: false,
    signUpError: false
};

class RegistrationContainer extends Component {
    constructor ( props ) {
        super( props );
        this.state = initialState;

        this.handleChange = this.handleChange.bind( this );
        this.handleNewsletterSignup = this.handleNewsletterSignup.bind( this );

        this.handleRouteChange = this.handleRouteChange.bind( this );

        this.setUserAddresses = this.setUserAddresses.bind( this );
        this.handleGoBack = this.handleGoBack.bind( this );
        this.setCounty = this.setCounty.bind( this );
        this.setStep = this.setStep.bind( this );
        this.setDentalLicense = this.setDentalLicense.bind( this );
        this.set_registration_status = this.set_registration_status.bind( this );

        this.registration_locked = this.registration_locked.bind( this );

        this.get_registration_status = this.get_registration_status.bind( this );
    }

    authorized () {
        const {state, props} = this;
        const {location, history} = this.props;

        let accountSetUpAuthorized = state.dentistFullName !== ''
            && state.dentalLicense !== ''
            && state.emailAddress !== ''
            && state.password !== '';

        return {
            newExistingDecision: function () {
                return accountSetUpAuthorized;
            },
            officeDetails: function () {
                return accountSetUpAuthorized;
            },
            verifyAccount: function () {
                return accountSetUpAuthorized;
            },
            accountRequiresVerification: function () {
                return history.location.state && history.location.state.emailAddress != null;
            },
            accountVerifiedNPI: function () {
                return history.location.state && history.location.state.emailAddress != null;
            },
            notValidated: function () {
                return state.addresses.length !== 0;
            },
            multipleAddresses: function () {
                return state.addresses.length !== 0 && state.addresses.length > 1;
            }
        };
    }

    setCounty ( county ) {
        this.setState( {county} );
    }

    setStep ( step ) {
        this.setState( {step} );
    }

    resetForm () {
        this.setState( initialState );
    }

    setDentalLicense ( value ) {
        this.setState( {dentalLicenseExists: value} );
    }

    setUserAddresses ( addresses ) {
        this.setState( {addresses} );
    }

    handleGoBack () {
        this.props.history.goBack();
    }

    handleChange = e => {
        this.setState( {[e.target.name]: e.target.value} );
    };

    handleNewsletterSignup () {
        this.setState( {signUpSubmitting: true, signUpLoading: true} );
        NewsletterService.subscribe( this.props.location.state.emailAddress )
            .then( resp => {
                dataLayer.forms.formSubmit( {
                    formName: 'Newsletter'
                } );
                this.setState( {
                    signUpSubmitting: false,
                    signUpLoading: false,
                    signUpSuccess: resp,
                    signUpError: false
                } );
            } )
            .catch( error => {
                appLogger.capture( error );
                this.setState( {
                    signUpSubmitting: false,
                    signUpLoading: false,
                    signUpSuccess: false,
                    signUpError: true
                } );
                console.log( error );
            } );
    }

    registration_locked () {
        return this.get_registration_status();
    }

    set_registration_status ( status ) {
        const locked = status && 'locked';
        this.setState( {step: locked} );
        Cookies.set( COOKIES.REGISTRATION.LOCKED.name, status, {expires: 1} );
    }

    get_registration_status () {
        return Cookies.get( COOKIES.REGISTRATION.LOCKED.name );
    }

    scrollToTop () {
        if ( this.scrollEl ) {
            this.scrollEl.scrollTop = 0;
            window.scrollTop = 0;
        }
    }

    handleRouteChange = ( event ) => {
        const viewCount = this.state.viewCount + 1;
        const detail = {
            step: event.target.location.hash,
            viewCount
        };
        dataLayer.user.sendUserRegistrationProgress( detail );

        this.setState( {viewCount} );
    };

    componentDidMount ( e ) {
        this.scrollEl = document.querySelector( $CONTENT_WINDOW );

        // Set up native browser event listeners
        window.addEventListener( 'popstate', this.handleRouteChange );

        const detail = {
            step: '/',
            viewCount: this.state.viewCount
        };
        dataLayer.user.sendUserRegistrationProgress( detail );
    }

    componentDidUpdate ( previousProps, prevState ) {
        if ( this.props.location.pathname !== previousProps.location.pathname ) {
            this.scrollToTop();
        }
    }

    componentWillUnmount () {
        // Clean up the native browser event listeners
        window.removeEventListener( 'popstate', this.handleRouteChange );
    }

    render () {
        let registrationLockedRoute = this.state.accountNumber === '' && '/';
        const isRegistrationLocked = this.get_registration_status();

        return (
            <div className="w-full md:max-w-sm mx-auto my-8 md:my-16">
                <img className="block my-4 md:my-12 mx-auto h-auto w-24 md:w-32"
                     src="/svg/site-logos/upi-icon-color-tagline.svg"
                     alt="Ultradent Products Logo"/>

                <Provider
                    client={client}
                    state={this.state}
                    signUp={this.handleNewsletterSignup}
                    handleChange={this.handleChange}
                    setUserAddresses={this.setUserAddresses}
                    handleGoBack={this.handleGoBack}
                    setCounty={this.setCounty}
                    setStep={this.setStep}
                    setDentalLicense={this.setDentalLicense}
                    setRegistrationStatus={this.set_registration_status}
                    getRegistrationStatus={this.get_registration_status}
                    registrationLocked={this.registration_locked}
                    resetForm={this.resetForm.bind( this )}>

                    <Switch>
                        {isRegistrationLocked
                         ? <Route path="/" component={RegistrationLocked}/>
                         : <Route exact path="/" component={AccountSetUp}/>
                        }


                        <ProtectedRoute authorized={() => {
                            return this.authorized().newExistingDecision();
                        }} path="/newExistingDecision" component={NewExistingDecision}/>

                        <ProtectedRoute authorized={() => {
                            return this.authorized().officeDetails();
                        }} path="/officeDetails" component={OfficeDetails}/>

                        <ProtectedRoute authorized={() => {
                            return this.get_registration_status();
                        }} path="/registrationLocked" redirectTo={registrationLockedRoute}
                                        component={RegistrationLocked}/>

                        <ProtectedRoute authorized={() => {
                            return this.authorized().verifyAccount();
                        }} path="/verifyAccount" component={VerifyAccount}/>

                        <ProtectedRoute authorized={() => {
                            return this.authorized().accountRequiresVerification();
                        }} path="/accountRequiresVerification" component={AccountRequiresVerification}/>

                        <ProtectedRoute authorized={() => {
                            return this.authorized().accountVerifiedNPI();
                        }} path="/accountVerifiedNPI" component={AccountVerifiedNPI}/>

                        <ProtectedRoute authorized={() => {
                            return this.authorized().notValidated();
                        }} path="/notValidated" component={NotValidated}/>

                        <ProtectedRoute authorized={() => {
                            return this.authorized().multipleAddresses();
                        }} path="/multipleAddresses" component={MultipleAddresses}/>
                    </Switch>

                </Provider>
            </div>
        );
    }
}

export default withRouter( RegistrationContainer );